<template>
  <DropMenu
    v-if="show"
    @close="show = false"
    :style="{ top: top + 'px', left: left + 'px' }"
    :items="items"
    :close-on-click="true"
    ref="drop-menu"
  >
  </DropMenu>
</template>

<script lang="ts" setup>
import { computed, nextTick, ref, useTemplateRef, watch } from 'vue';

import { Dialog } from '../classes';
import {
  deleteNotes,
  deleteSelectedNotes,
  exportNotes,
  findNote,
  importNotesFromFileChooser,
  noteState,
} from '../store/note';
import { DropMenuItemData } from '../types';
import { isEmptyNote } from '../utils';

import DropMenu from './DropMenu.vue';

const props = defineProps<{
  handleNewNote: () => void;
  ev?: MouseEvent | PointerEvent;
}>();

const dropMenu = useTemplateRef('drop-menu');

const clickedNoteUuid = ref<string>();
const show = ref(false);
const top = ref(0);
const left = ref(0);

const items = computed<DropMenuItemData[]>(() => {
  const hasNoNotes = noteState.notes.length === 1 && isEmptyNote(noteState.notes[0]);

  return [
    {
      label: 'New Note',
      clickHandler: props.handleNewNote,
      testId: 'new',
    },
    {
      label: 'Export Note',
      clickHandler: handleExportNotes,
      showIf: () => !!clickedNoteUuid.value && !hasNoNotes,
      testId: 'export',
    },
    {
      label: 'Export All Notes',
      testId: 'export-all',
      clickHandler: () => exportNotes(noteState.notes.map((nt) => nt.uuid)),
    },
    {
      label: 'Import Notes',
      testId: 'import',
      // NOTE: This needs to be called within an arrow function,
      //       so it can be spied on in tests.
      clickHandler: () => importNotesFromFileChooser(),
    },
    {
      label: noteState.extraSelectedNotes.length ? 'Delete Notes' : 'Delete Note',
      clickHandler: handleDeleteNote,
      showIf: () => !!clickedNoteUuid.value && !hasNoNotes,
      testId: 'delete',
      danger: true,
    },
  ];
});

function handleExportNotes() {
  if (noteState.extraSelectedNotes.length > 0) {
    exportNotes([
      noteState.selectedNote.uuid,
      ...noteState.extraSelectedNotes.map((nt) => nt.uuid),
    ]);
  } else if (clickedNoteUuid.value) {
    exportNotes([clickedNoteUuid.value]);
  }
}

async function handleDeleteNote() {
  const confirmed = await Dialog.ask('Are you sure?', {
    title: 'Delete Note(s)',
    kind: 'warning',
  });
  if (!confirmed) return;

  if (noteState.extraSelectedNotes.length > 0) {
    deleteSelectedNotes();
  } else if (clickedNoteUuid.value) {
    const clickedNote = findNote(clickedNoteUuid.value);
    if (!clickedNote) return;

    deleteNotes([clickedNote]);
  }
}

watch(props, async () => {
  if (!props.ev) return;

  const target = props.ev.target as HTMLElement | null;
  const closestNote = target?.closest<HTMLElement>('[data-note-uuid]');

  show.value = true;
  clickedNoteUuid.value = closestNote?.dataset.noteUuid;

  // Wait for `DropMenu` to mount, so we can read height
  await nextTick();

  const dropMenuHeight = dropMenu.value?.$el.clientHeight ?? 120; // Fallback for tests
  const dropMenuWidth = dropMenu.value?.$el.clientWidth ?? 200; // Fallback for tests
  const maxY = window.innerHeight - dropMenuHeight - 10; // 10 = a bit of padding
  const maxX = window.innerWidth - dropMenuWidth - 10; // 10 = a bit of padding

  top.value = Math.min(props.ev.clientY, maxY);
  left.value = Math.min(props.ev.clientX, maxX);
});
</script>

<style lang="scss" scoped></style>
